/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useState} from "react";
import {Route, Switch, useLocation} from "react-router";
import {getQueryParams} from "helpers/helpers";
import PageNotFound from "pages/Front/PageNotFound/PageNotFound";

import {useTypedSelector} from "helpers/reducers";
import SessionAction from "store/session/SessionAction";
import {useDispatch} from "useDispatch";
import ToastContainer from "components/ToastContainer";
import {useTranslation} from "react-i18next";
import {Flip, toast} from "react-toastify";
import UnloggedLayout from "./UnloggedLayout";
import ArticlesAction from "../store/articles/ArticlesAction";
import {clearCache, clearLocalStorage} from "helpers/store";
import {DEFAULT_BASENAME, HAS_BASENAME, languages} from "helpers/config";
import ReservationAction from "store/reservations/ReservationAction";
import {BrowserRouter} from "react-router-dom";
import {Helmet} from "react-helmet";
import useNavigation from "utils/hooks/useNavigation";

import {useCookies} from "utils/hooks/useCookies";
import UnloggedLayoutNew from "layouts/UnloggedLayoutNew";
import {User} from "types/entities/User";
import UnsubscribeNewsletter from "pages/Front/Booking/UnsubscribeNewsletter/UnsubscribeNewsletter";
import UnsubscribeContainer from "pages/Front/Booking/UnsubscribeNewsletter/UnsubscribeContainer";

const BookingNew = React.lazy(() => import("pages/Front/Booking/BookingNew"));

const FrontLayout = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const session = useTypedSelector(state => state.sessionState.session);
    const user = (session?.user as User) ?? null;
    const profile = useTypedSelector(state => state.sessionState.profile);
    const language = profile && languages.find(language => language.code === profile.lang);
    const location = useLocation();
    const navigation = useNavigation();
    const [shopActive, setShopActive] = useState(false);
    const {cookies, cookiesEnabled} = useCookies();
    // useEffect(() => {
    //     document.title = location.pathname.includes("/booking") ? "Booking" : "Online profile";
    // }, [location.pathname]);

    const getLanguage = (lang_string: string) => {
        return languages.find(language => language.code === lang_string);
    };
    useEffect(() => {
        const queryParams = getQueryParams();
        if (queryParams?.connected) {
            toast.success(t("Connected successfully"));
        }

        if (queryParams?.lang == "en") {
            const lang = getLanguage("en");
            if (lang) {
                dispatch(SessionAction.setLanguage(lang));
            }
        }
        if (session && session.user && session.user.user_type && session.user.user_type !== "guest" && user.username !== "visitor") {
            dispatch(SessionAction.fetchProfile())
                .then(r => {
                    if (language) {
                        dispatch(SessionAction.setLanguage(language));
                    }
                    if (r.status === 401) {
                        dispatch(SessionAction.logout()).then(r => {
                            clearLocalStorage();
                            clearCache();
                            window.location.href = "/";
                        });
                    }
                })
                .catch(err => {
                    console.log("error on session fetchProfile");
                });

            if (!location.pathname.includes("booking")) {
                dispatch(ReservationAction.fetchReservations("current", 1, 10));
                dispatch(ArticlesAction.fetchArticlesWithHall());
                dispatch(SessionAction.fetchNotifications()).then(r => {});
            }
        }
    }, [session]);

    const matomoAnalytics = () => {
        // @ts-ignore
        var _mtm = (window._mtm = window._mtm || []);
        _mtm.push({"mtm.startTime": new Date().getTime(), event: "mtm.Start"});
        var d = document,
            g = d.createElement("script"),
            s = d.getElementsByTagName("script")[0];
        g.async = true;
        g.src = "https://cl.arbopark.ch/stats/matomo/js/container_ceMbFaOY.js";
        // @ts-ignore
        s.parentNode.insertBefore(g, s);
    };
    useEffect(() => {
        if (session && session.user && session.user.user_type && session.user.user_type !== "guest") {
            dispatch(SessionAction.fetchNotifications()).then(r => {});
        }
    }, [location.pathname]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location.pathname]);

    useEffect(() => {
        if (cookies && cookiesEnabled) {
            matomoAnalytics();
        }
    }, [cookies]);

    useEffect(() => {
        //@AI if location.pathname doesnt begin with "/booking", redirect to it
        if (!location.pathname.startsWith("/unsubscribe") && !location.pathname.startsWith("/booking")) {
            window.location.href = "/booking";
        }
    }, []);

    return (
        <BrowserRouter basename={HAS_BASENAME ? "/" + DEFAULT_BASENAME : "/"}>
            <Switch>
                <Route path="/booking">
                    <Helmet>
                        <title>{navigation.titleText(t("Booking"))}</title>
                    </Helmet>

                    <UnloggedLayoutNew>
                        <BookingNew />
                    </UnloggedLayoutNew>
                </Route>

                <Route path="/unsubscribe">
                    <Helmet>
                        <title>{navigation.titleText(t("Newsletter"))}</title>
                    </Helmet>

                    <UnloggedLayoutNew>
                        <UnsubscribeContainer>
                            <UnsubscribeNewsletter />
                        </UnsubscribeContainer>
                    </UnloggedLayoutNew>
                </Route>

                <Route>
                    <Helmet>
                        <title>{t("Page not found")}</title>
                    </Helmet>
                    <UnloggedLayout>
                        <PageNotFound />
                    </UnloggedLayout>
                </Route>
            </Switch>
            <ToastContainer autoClose={5000} draggablePercent={50} newestOnTop={true} pauseOnFocusLoss={false} transition={Flip} />
        </BrowserRouter>
    );
};

export default FrontLayout;
